import { useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

interface NavigationHistory {
  showBackButton: boolean;
  backButtonText: string;
  onBack: () => void;
}

// Capitalize first letter for display
const capitalizeTab = (tab: string): string => {
  return tab.charAt(0).toUpperCase() + tab.slice(1);
};

export const useNavigationHistory = (): NavigationHistory => {
  const history = useHistory();
  const location = useLocation();

  const navigationState = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const fromParam = searchParams.get('from');

    if (fromParam) {
      return {
        showBackButton: true,
        backButtonText: `Back to ${capitalizeTab(fromParam)}`,
        sourceTab: fromParam,
      };
    }

    return {
      showBackButton: false,
      backButtonText: '',
      sourceTab: null,
    };
  }, [location.search]);

  const onBack = () => {
    if (!navigationState.showBackButton || !navigationState.sourceTab) {
      return;
    }

    const searchParams = new URLSearchParams(location.search);
    const fromUrl = searchParams.get('fromUrl');

    if (fromUrl) {
      // Navigate to the exact previous URL state
      try {
        const decodedUrl = decodeURIComponent(fromUrl);
        const [pathname, search] = decodedUrl.split('?');
        history.push({
          pathname,
          search: search ? `?${search}` : '',
        });
      } catch (error) {
        // Fallback if URL decoding fails
        console.warn('Failed to decode fromUrl parameter:', error);
        history.push({
          pathname: `/social/analytics/${navigationState.sourceTab}`,
          search: '',
        });
      }
    } else {
      // Fallback: navigate to source tab without preserving state
      history.push({
        pathname: `/social/analytics/${navigationState.sourceTab}`,
        search: '',
      });
    }
  };

  return {
    showBackButton: navigationState.showBackButton,
    backButtonText: navigationState.backButtonText,
    onBack,
  };
};

export default useNavigationHistory;
